// The Version Cohorts section's vocabulary and its pure presentation logic.
//
// The window values are the generated `CohortWindow` wire union, so renaming one
// on the backend fails typecheck here; this module adds display order, labels,
// the URL-param parser shared by the route and the page, and the formatters.
//
// NOTHING here computes a metric. Every mean, delta and percentage arrives from
// the backend already divided (`web/routes/admin_version_cohorts.py`), because a
// metric's definition belongs in one place and a page that recomputes its own is
// a second definition that agrees right up until one of them changes. These
// functions only decide how an already-decided number READS.
import type {
  CohortBreakdownRow,
  CohortMetric,
  CohortSeriesCount,
  CohortSide,
  CohortSplit,
  CohortWindow,
  DeployMark,
  MetricUnit,
} from "@recruiter/shared/types";

import { countryLabel } from "./country";
import { deviceLabel } from "./device";
import { formatClock, formatDate } from "./format";

export const COHORT_WINDOWS: readonly CohortWindow[] = ["7d", "14d", "30d"];

export const COHORT_WINDOW_LABELS: Record<CohortWindow, string> = {
  "7d": "7 days either side",
  "14d": "14 days either side",
  "30d": "30 days either side",
};

export function isCohortWindow(value: unknown): value is CohortWindow {
  return typeof value === "string" && (COHORT_WINDOWS as readonly string[]).includes(value);
}

/** `?window=` parser: a known value or absent (the route defaults to "14d"). */
export function parseCohortWindow(value: unknown): CohortWindow | undefined {
  return isCohortWindow(value) ? value : undefined;
}

/** The page's SUBJECTS, in display order — mirrors `SECTIONS` in
 *  `backend/recruiter/repo/version_cohorts.py`, where every metric names one via
 *  its `section` field. The page renders ONE subject at a time (nineteen cards
 *  on one page stopped being readable); `other` is the trailing catch-all for a
 *  metric whose section this bundle does not know — a newer backend, or a
 *  snapshot cached before sections shipped — so a card can move tabs but never
 *  disappear. */
export const COHORT_SECTIONS = [
  "results",
  "search",
  "supply",
  "journey",
  "talent",
  "clients",
] as const;

export type CohortSection = (typeof COHORT_SECTIONS)[number] | "other";

export const COHORT_SECTION_LABELS: Record<CohortSection, string> = {
  results: "Results funnel",
  search: "Search quality & cost",
  supply: "Marketplace supply",
  journey: "Client journey",
  talent: "Talent outreach",
  clients: "Clients: mix & opt-ins",
  other: "More",
};

/** Which subject a metric files under: its own `section` when this bundle knows
 *  it, the trailing `other` group when it does not. */
export function sectionOf(metric: Pick<CohortMetric, "section">): CohortSection {
  const section = metric.section;
  return (COHORT_SECTIONS as readonly string[]).includes(section)
    ? (section as CohortSection)
    : "other";
}

/** `?section=` parser: a known subject or absent (the page falls back to the
 *  first subject that actually has metrics). `other` is addressable on purpose:
 *  the tab only renders when non-empty, but a shared link to it must survive a
 *  reload rather than snapping to a different comparison than it named. */
export function parseCohortSection(value: unknown): CohortSection | undefined {
  return typeof value === "string" &&
    ((COHORT_SECTIONS as readonly string[]).includes(value) || value === "other")
    ? (value as CohortSection)
    : undefined;
}

/** How many characters of a release id to show. A commit sha is 40 characters and
 *  nobody reads past the first seven; a Cloud Run revision name is already short
 *  and is left whole by the length check in `shortRelease`. */
const SHA_PREFIX = 7;

/** The readable form of a release id: a 40-char commit sha shortened to its
 *  familiar prefix, anything else (a revision name) left alone. */
export function shortRelease(release: string): string {
  return /^[0-9a-f]{40}$/i.test(release) ? release.slice(0, SHA_PREFIX) : release;
}

/** One line for the deploy dropdown: when it started serving, and what it was.
 *
 *  The DATE leads because that is what an operator is actually choosing between
 *  ("the one from Tuesday"), and the release id trails as the disambiguator for
 *  two deploys on the same day.
 *
 *  Built from the app's OWN `formatDate`/`formatClock` rather than a local
 *  `toLocaleString` with its own options: those follow the viewer's locale, and a
 *  dropdown reading "17 Jul" beside a page reading "7/17/2026" looks like two
 *  different dates to the person choosing between them. A mark with no usable
 *  timestamp cannot happen (the column is NOT NULL) but says so rather than
 *  rendering "Invalid Date". */
export function deployLabel(mark: DeployMark): string {
  const day = formatDate(mark.firstSeenAt ?? null);
  const time = formatClock(mark.firstSeenAt ?? null);
  const stamp = day === "-" || time === "-" ? "unknown time" : `${day}, ${time}`;
  return `${stamp} · ${shortRelease(mark.release)}`;
}

/** A metric's mean, in its own units. Null is "-", never 0: an unmeasured side is
 *  UNKNOWN, and printing zero would read as a metric that collapsed. */
export function formatMetric(value: number | null, unit: MetricUnit): string {
  if (value === null || !Number.isFinite(value)) return "-";
  switch (unit) {
    case "usd":
      // SCOUT costs cents per search, so whole dollars would round every value to
      // $0. Three decimals is what actually distinguishes one release from the next.
      return value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(3)}`;
    case "rate":
      return `${Math.round(value * 1000) / 10}%`;
    case "hours":
      // Under an hour, minutes are the honest unit; over two days, days are.
      if (value < 1) return `${Math.round(value * 60)} min`;
      if (value >= 48) return `${Math.round((value / 24) * 10) / 10} days`;
      return `${Math.round(value * 10) / 10} h`;
    case "minutes":
      if (value >= 120) return `${Math.round((value / 60) * 10) / 10} h`;
      return `${Math.round(value * 10) / 10} min`;
    case "turns":
      return `${Math.round(value * 10) / 10}`;
    case "score":
      // The denominator is printed rather than assumed. A bare "72" beside a "0.31"
      // and a "$0.042" invites the reader to guess at a scale, and the two guesses
      // available here (out of 100, or out of 10) are a factor of ten apart.
      return `${Math.round(value * 10) / 10} / 100`;
    case "count":
      return `${Math.round(value * 10) / 10}`;
  }
}

/** The change, as a signed percentage: "+12%", "-4.5%", "-" when unknowable.
 *
 *  Percent rather than absolute, because the six metrics are in five different
 *  units and a reader scanning the column wants "did it move, and by how much of
 *  itself" — the absolute figures sit beside it for anyone who wants them. */
export function formatDeltaPct(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return "-";
  const magnitude = Math.abs(pct * 100);
  const rounded = magnitude < 10 ? Math.round(magnitude * 10) / 10 : Math.round(magnitude);
  if (rounded === 0) return "no change";
  return `${pct < 0 ? "-" : "+"}${rounded}%`;
}

/** How confident the comparison is, as plain words rather than a statistic.
 *
 *  This is deliberately NOT a significance test. A p-value here would be theatre:
 *  the two sides are consecutive calendar windows, not randomized arms, so the
 *  assumptions behind the test do not hold and the number would carry an
 *  authority the design cannot support. What a reader genuinely needs to know is
 *  whether there is enough data to bother reading, so that is what this says. */
export function evidenceNote(beforeCount: number, afterCount: number): string | null {
  const weakest = Math.min(beforeCount, afterCount);
  if (weakest === 0) return "No data on one side yet.";
  if (weakest < 20) return `Thin: ${weakest} observations on the smaller side.`;
  return null;
}

export interface SplitPart {
  /** What this lane is CALLED - the metric's own name for it, never one this
   *  module invented. */
  label: string;
  /** How many observations went this way. A whole number: the wire carries the
   *  numerator as a float sum of ones and zeros, and a JSON round-trip of that is
   *  allowed to come back as 6.999999999. */
  count: number;
  /** This lane's share of the side, 0..1 - the bar's own geometry. */
  share: number;
}

/** The two named populations behind one side of a two-outcome RATE: how many
 *  observations went each way.
 *
 *  A share is not an answer to "how many chose what", and this page is otherwise
 *  made entirely of shares. The counts are already in the payload - `total` is the
 *  numerator and `count` the denominator - so this is not a second definition of
 *  the metric, it is the same two numbers stated as populations. The NAMES come
 *  from the backend's own spec (`split`) for the same reason the mean is divided
 *  there: whichever lane the query's `CASE` counts is a backend fact, and a page
 *  that decided it locally would keep printing the old labels after somebody
 *  flipped the `CASE`, which is a chart that reads exactly backwards.
 *
 *  Null when the side observed nothing - UNKNOWN, and explicitly not two zeros,
 *  the same rule `mean` follows. Drawing an empty side as 0 and 0 would show a
 *  release with no traffic yet as a lane nobody picked.
 *
 *  The numerator is CLAMPED into `[0, count]`. It cannot exceed the denominator by
 *  any honest arithmetic, but a stale snapshot read against a re-tuned query is
 *  not arithmetic, and a negative remainder would render as a bar segment growing
 *  backwards rather than as the drift it is. */
export function splitParts(side: CohortSide, split: CohortSplit): SplitPart[] | null {
  if (side.count <= 0) return null;
  const counted = Math.min(Math.max(Math.round(side.total), 0), side.count);
  const rest = side.count - counted;
  return [
    { label: split.counted, count: counted, share: counted / side.count },
    { label: split.rest, count: rest, share: rest / side.count },
  ];
}

export interface SparkPoint {
  x: number;
  y: number;
}

/** Lay a metric's daily series out in a unit box (0..1 on both axes), skipping
 *  days with no data.
 *
 *  Y is INVERTED (0 is the top) so the caller can use the result as SVG
 *  coordinates directly. A flat series is drawn down the middle rather than
 *  divided by a zero range.
 *
 *  Returns the points only; the caller owns the path string, because the boundary
 *  marker has to line up with the same x scale and that is easier to get right
 *  when one function owns the scale. */
export function sparkPoints(metric: CohortMetric): SparkPoint[] {
  const days = metric.series;
  if (days.length === 0) return [];
  const values = days.map((d) => d.mean).filter((v): v is number => v !== null);
  if (values.length === 0) return [];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const range = hi - lo;
  const out: SparkPoint[] = [];
  days.forEach((point, i) => {
    if (point.mean === null) return;
    const x = days.length === 1 ? 0.5 : i / (days.length - 1);
    const y = range === 0 ? 0.5 : 1 - (point.mean - lo) / range;
    out.push({ x, y });
  });
  return out;
}

/** Where the boundary day sits on the same 0..1 x scale `sparkPoints` uses, or
 *  null when the series does not reach it.
 *
 *  Matching on the DAY string rather than interpolating a timestamp keeps this
 *  aligned with the buckets: the series is day-grained, so a boundary drawn
 *  fractionally between two days would claim a precision the data does not have. */
export function boundaryX(metric: CohortMetric, boundaryDay: string | null): number | null {
  if (!boundaryDay) return null;
  const days = metric.series;
  const index = days.findIndex((d) => d.day >= boundaryDay);
  if (index < 0 || days.length <= 1) return null;
  return index / (days.length - 1);
}

/** The UTC day a boundary timestamp falls on, for `boundaryX`. UTC because the
 *  buckets are keyed by UTC date; using the viewer's local day would slide the
 *  marker a day whenever they are west of Greenwich in the evening. */
export function boundaryDayOf(firstSeenAt: string | null | undefined): string | null {
  if (!firstSeenAt) return null;
  const at = new Date(firstSeenAt);
  if (Number.isNaN(at.getTime())) return null;
  return at.toISOString().slice(0, 10);
}

// --- The breakdown card: a MIX either side of the deploy ---------------------------

/** How many dimension values a breakdown card lists before folding the rest. Six
 *  lines is what a card-width chart can keep apart; the tail is real and is kept as
 *  ONE "Other" row rather than dropped, so the shares still add up to the whole. */
export const BREAKDOWN_HEAD = 6;

/** The folded tail's key. Not a wire value: the backend never names a value this. */
export const BREAKDOWN_OTHER = "__other__";

export interface BreakdownLine {
  key: string;
  label: string;
  before: { count: number; share: number | null };
  after: { count: number; share: number | null };
  deltaShare: number | null;
  /** Null for the folded tail, which has no series of its own to draw. */
  series: readonly CohortSeriesCount[] | null;
}

/** How a dimension VALUE reads: a country code through the runtime's region names,
 *  a device class through the device vocabulary, anything else as itself. */
export function dimensionLabel(dimension: string | null | undefined, key: string): string {
  if (key === BREAKDOWN_OTHER) return "Other";
  if (dimension === "country") return countryLabel(key);
  if (dimension === "device") return deviceLabel(key);
  return key;
}

function fold(rows: readonly CohortBreakdownRow[], side: "before" | "after") {
  const count = rows.reduce((total, row) => total + row[side].count, 0);
  const shares = rows.map((row) => row[side].share);
  // Unknown stays unknown: a side with no projects has null shares on every row,
  // and summing nulls into a 0 would draw the tail as "nobody" rather than "unmeasured".
  const share = shares.every((share) => share === null)
    ? null
    : shares.reduce<number>((total, share) => total + (share ?? 0), 0);
  return { count, share };
}

/** The rows a breakdown card shows: the head of the backend's volume-ordered list,
 *  plus the rest folded into one "Other" row whose counts and shares are the sums of
 *  what it hides. NOTHING here re-derives a share — the backend divided each row by
 *  its side's total; this only adds already-decided fractions together. */
export function breakdownLines(
  metric: Pick<CohortMetric, "breakdown" | "dimension">,
  head: number = BREAKDOWN_HEAD,
): BreakdownLine[] {
  const rows = metric.breakdown ?? [];
  const lines: BreakdownLine[] = rows.slice(0, head).map((row) => ({
    key: row.key,
    label: dimensionLabel(metric.dimension, row.key),
    before: row.before,
    after: row.after,
    deltaShare: row.deltaShare,
    series: row.series,
  }));
  const rest = rows.slice(head);
  if (rest.length > 0) {
    const before = fold(rest, "before");
    const after = fold(rest, "after");
    lines.push({
      key: BREAKDOWN_OTHER,
      label: dimensionLabel(metric.dimension, BREAKDOWN_OTHER),
      before,
      after,
      deltaShare:
        before.share !== null && after.share !== null ? after.share - before.share : null,
      series: null,
    });
  }
  return lines;
}

/** A share as the page prints it: "38.2%", or "-" for an unknown side. */
export function formatShare(share: number | null): string {
  if (share === null || !Number.isFinite(share)) return "-";
  return `${Math.round(share * 1000) / 10}%`;
}

/** A share CHANGE in percentage points ("+3.2 pp"), which is what a mix that moved
 *  is measured in — a relative change of a share would read a 1% -> 2% country as
 *  the biggest mover on the card. */
export function formatShareDelta(delta: number | null): string {
  if (delta === null || !Number.isFinite(delta)) return "-";
  const points = Math.round(delta * 1000) / 10;
  if (points === 0) return "no change";
  return `${points < 0 ? "-" : "+"}${Math.abs(points)} pp`;
}

export interface BreakdownPolyline {
  key: string;
  points: SparkPoint[];
}

/** The days any shown line has a point on, oldest first: the one x scale every
 *  polyline and the boundary marker share. */
export function breakdownDays(lines: readonly BreakdownLine[]): string[] {
  return [...new Set(lines.flatMap((line) => (line.series ?? []).map((point) => point.day)))].sort();
}

/** One unit-box polyline per shown row, on ONE y scale (the largest daily count
 *  among them) and ONE x scale (`breakdownDays`), so a line's height is comparable
 *  to the line beside it — the whole point of drawing them together. Y is inverted
 *  (0 is the top), as `sparkPoints` does, so the caller can use the result as SVG
 *  coordinates directly. A day a line has no point on is a gap, never a zero. */
export function breakdownPolylines(lines: readonly BreakdownLine[]): BreakdownPolyline[] {
  const days = breakdownDays(lines);
  if (days.length === 0) return [];
  const index = new Map(days.map((day, i) => [day, i]));
  const peak = Math.max(0, ...lines.flatMap((line) => (line.series ?? []).map((p) => p.count)));
  return lines
    .filter((line) => line.series !== null && line.series.length > 0)
    .map((line) => ({
      key: line.key,
      points: (line.series ?? []).map((point) => ({
        x: days.length === 1 ? 0.5 : (index.get(point.day) ?? 0) / (days.length - 1),
        y: peak === 0 ? 1 : 1 - point.count / peak,
      })),
    }));
}

/** Where the boundary day sits on `breakdownDays`' 0..1 x scale, or null when the
 *  series does not reach it — `boundaryX`'s twin over the union of the lines' days. */
export function breakdownBoundaryX(days: readonly string[], boundaryDay: string | null): number | null {
  if (!boundaryDay) return null;
  const index = days.findIndex((day) => day >= boundaryDay);
  if (index < 0 || days.length <= 1) return null;
  return index / (days.length - 1);
}

export type { CohortWindow };
